import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Title,
  Text,
  TextInput,
  Group,
  MultiSelect,
  Checkbox,
  Pagination,
  Badge,
  MantineProvider,
  ActionIcon,
  Drawer,
  Button,
  Stack,
  Divider,
} from '@mantine/core';
import { Head } from '@inertiajs/react';
import { IconSearch, IconAdjustmentsHorizontal } from '@tabler/icons-react';
import ArtistCard from '../../components/Cards/ArtistCard';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import '../../styles/ShowView.css';

interface DanceStyle {
  id: number;
  name: string;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  avatar_url?: string;
  instructor: boolean;
  dj: boolean;
  dance_styles?: DanceStyle[];
  dance_style_ids?: number[];
  description?: string | { body: string };
  [key: string]: any;
}

interface ArtistIndexProps {
  artists: Artist[];
  totalArtists: number;
  danceStyles: DanceStyle[];
}

export default function ArtistIndex({ artists, totalArtists, danceStyles }: ArtistIndexProps) {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDanceStyles, setSelectedDanceStyles] = useState<string[]>([]);
  const [isInstructor, setIsInstructor] = useState(false);
  const [isDj, setIsDj] = useState(false);

  // State for drawer
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);

  // State for filtered artists
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>(artists);

  // State for dynamic drawer
  const [artistDrawerOpened, setArtistDrawerOpened] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

  // Calculate active filter count
  const activeFilterCount = selectedDanceStyles.length + (isInstructor ? 1 : 0) + (isDj ? 1 : 0);

  // Effect to filter artists when search or filter criteria change
  useEffect(() => {
    let result = [...artists];

    // Filter by name
    if (searchTerm) {
      result = result.filter((artist) => artist.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Filter by dance styles
    if (selectedDanceStyles.length > 0) {
      result = result.filter((artist) => {
        // Check if artist has dance_styles array with objects
        if (artist.dance_styles?.length > 0) {
          return artist.dance_styles.some((style) => selectedDanceStyles.includes(style.id.toString()));
        }
        // Check if artist has dance_style_ids array with IDs
        else if (artist.dance_style_ids?.length > 0) {
          return artist.dance_style_ids.some((styleId) => selectedDanceStyles.includes(styleId.toString()));
        }
        return false;
      });
    }

    // Filter by instructor/DJ
    if (isInstructor) {
      result = result.filter((artist) => artist.instructor);
    }

    if (isDj) {
      result = result.filter((artist) => artist.dj);
    }

    setFilteredArtists(result);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchTerm, selectedDanceStyles, isInstructor, isDj, artists]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredArtists.length / itemsPerPage);
  const paginatedArtists = filteredArtists.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Handle artist card click
  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setArtistDrawerOpened(true);
  };

  // Handle artist drawer close
  const handleArtistDrawerClose = () => {
    setArtistDrawerOpened(false);
  };

  // Handle clear all filters
  const handleClearFilters = () => {
    setSelectedDanceStyles([]);
    setIsInstructor(false);
    setIsDj(false);
  };

  // Format dance styles for MultiSelect
  const danceStyleOptions = danceStyles.map((style) => ({
    value: style.id.toString(),
    label: style.name,
  }));

  return (
    <>
      <MantineProvider>
        <Head title="Artists" />

        <Container size="md" mt="md">
          {/* Search Section */}
          <Box className="filter-wrapper" mb="xl">
            <Group justify="start" align="center">
              <TextInput
                leftSection={<IconSearch size={14} />}
                placeholder="Search artists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                w={{ base: '80%', md: '600px' }}
              />

              {/* Filter Action Icon with Badge */}
              <Box pos="relative">
                <ActionIcon variant="light" size="xl" color="orange" onClick={() => setFilterDrawerOpened(true)}>
                  <IconAdjustmentsHorizontal />
                </ActionIcon>
                {activeFilterCount > 0 && (
                  <Badge
                    color="orange"
                    size="ms"
                    radius="xl"
                    style={{
                      position: 'absolute',
                      top: -15,
                      right: -10,
                      padding: '4px 8px',
                      minWidth: '24px',
                      height: '24px',
                    }}
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Box>
            </Group>
          </Box>

          {/* Results Section */}
          <Box className="results-wrapper">
            <Title order={5} mb="md" className="poppins" c="#f2f2f2">
              Currently showing {paginatedArtists.length} of {filteredArtists.length} artists.
            </Title>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} mb="md" color="orange" />
            )}

            {/* Artist Grid */}
            <Grid gutter="md">
              {paginatedArtists.map((artist) => (
                <Grid.Col span={{ base: 6, xs: 4, sm: 4, md: 4, lg: 3 }} key={artist.id}>
                  <ArtistCard artist={artist} onCardClick={handleArtistClick} />
                </Grid.Col>
              ))}
            </Grid>

            {/* Show message if no results */}
            {paginatedArtists.length === 0 && (
              <Text ta="center" fz="lg" fw={500} mt="xl">
                No artists found matching your search criteria.
              </Text>
            )}

            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} mt="xl" color="orange" />
            )}
          </Box>
        </Container>

        {/* Filter Drawer from bottom */}
        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          position="right"
          size="md"
          withCloseButton={true}
          style={{ zIndex: 1000 }}
          styles={{
            root: { zIndex: 1000 },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
            },
            close: {
              color: '#f2f2f2',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            },
          }}
        >
          <Stack gap="0" p="md" style={{ width: '100%', minHeight: '100vh' }}>
            <Text size="lg" fw={600} c="#f2f2f2" style={{ lineHeight: 1 }}>
              Filters
            </Text>

            <Divider mb="md" color="#333" />

            {/* Dance Styles Filter */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2">
                Dance Styles
              </Text>
              <MultiSelect
                data={danceStyleOptions}
                placeholder={selectedDanceStyles.length === 0 ? 'Select dance styles' : ''}
                value={selectedDanceStyles}
                onChange={setSelectedDanceStyles}
                clearable
                searchable
                hidePickedOptions
                variant="filled"
                size="md"
                w="100%"
                className="custom-select"
              />
            </Box>

            <Divider my="sm" color="#333" />

            {/* Artist Type Filters */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2" className="poppins">
                Artist Type
              </Text>
              <Group mt="xs">
                <Checkbox
                  label="Instructors"
                  checked={isInstructor}
                  color="#EA580C"
                  onChange={(e) => setIsInstructor(e.currentTarget.checked)}
                  styles={{
                    label: { color: '#f2f2f2' },
                    input: {
                      '&:checked': {
                        backgroundColor: '#EA580C',
                        borderColor: '#EA580C',
                      },
                    },
                  }}
                />
                <Checkbox
                  label="DJs"
                  checked={isDj}
                  color="#EA580C"
                  onChange={(e) => setIsDj(e.currentTarget.checked)}
                  styles={{
                    label: { color: '#f2f2f2' },
                    input: {
                      '&:checked': {
                        backgroundColor: '#EA580C',
                        borderColor: '#EA580C',
                      },
                    },
                  }}
                />
              </Group>
            </Box>

            <Divider my="md" color="#333" />

            {/* Bottom Action Row with Count and Clear Filters */}
            <Group justify="space-between" mt="lg">
              <Button
                variant="light"
                color="orange"
                size="md"
                onClick={handleClearFilters}
                disabled={activeFilterCount === 0}
                styles={{
                  root: {
                    borderColor: '#EA580C10',
                    '&:disabled': {
                      borderColor: '#3F3F46',
                      color: '#71717A',
                    },
                  },
                }}
              >
                Clear All Filters
              </Button>
              <Button
                variant="filled"
                color="#EA580C"
                radius="md"
                size="md"
                onClick={() => setFilterDrawerOpened(false)}
              >
                Show {filteredArtists.length} Artists
              </Button>
            </Group>
          </Stack>
        </Drawer>

        {/* Artist Dynamic Drawer */}
        {selectedArtist && (
          <DynamicDrawer
            opened={artistDrawerOpened}
            type="artist"
            data={selectedArtist}
            onClose={handleArtistDrawerClose}
          />
        )}
      </MantineProvider>
    </>
  );
}
